/**
 * API Client for iATM Marketplace
 * 
 * Handles all API requests to the backend, including authentication
 * and error handling.
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

/**
 * Global 401 handler - handles authentication failures
 * This will be set by the app to handle logout and redirect
 */
let global401Handler: (() => void) | null = null;

/**
 * Set the global 401 handler function
 * This should be called during app initialization with a function that:
 * - Calls logout from useAuth
 * - Redirects to /auth/login
 * @param handler - Function to call when 401 is detected
 */
export function set401Handler(handler: () => void) {
  global401Handler = handler;
}

/**
 * Generic API request function with 401 interceptor
 * Automatically handles 401 responses by triggering logout and redirect
 * @param url - API endpoint URL (relative to API_URL)
 * @param options - Fetch options
 * @returns Promise<Response>
 */
export async function apiRequest(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
  
  const response = await fetch(fullUrl, {
    ...options,
    credentials: 'include', // Always include HTTP-only cookies
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  // Handle 401 Unauthorized - token expired or invalid
  if (response.status === 401) {
    // Check if this is a 2FA endpoint (where 401 means invalid code, not auth failure)
    const is2FAEndpoint = url.includes('/twofactor/');
    
    if (!is2FAEndpoint && global401Handler) {
      // This is an authentication failure - trigger logout and redirect
      global401Handler();
    }
    // For 2FA endpoints, 401 means invalid code, so we let the caller handle it
    // For other endpoints, we've triggered logout, but still return the response
    // so the caller can handle the error appropriately
  }

  return response;
}

/**
 * Response type for sendTwoFactorCode
 */
export interface SendTwoFactorCodeResponse {
  success: boolean;
  message: string;
}

/**
 * Send 2FA verification code to email
 * @param email - User's email address
 * @returns Promise with success status and message
 * @throws Error if request fails
 */
export async function sendTwoFactorCode(
  email: string
): Promise<SendTwoFactorCodeResponse> {
  try {
    const response = await fetch(`${API_URL}/auth/twofactor/send`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      if (response.status === 400) {
        const error = await response.json().catch(() => ({ message: 'Invalid email address' }));
        throw new Error(error.message || 'Invalid email address');
      }
      throw new Error('Failed to send verification code');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Network error: Failed to send verification code');
  }
}

/**
 * Response type for verifyTwoFactorCode
 */
export interface VerifyTwoFactorCodeResponse {
  success: boolean;
  verified: boolean;
}

/**
 * Verify 2FA code
 * @param email - User's email address
 * @param code - 6-digit verification code
 * @returns Promise with success status and verification result
 * @throws Error if request fails
 */
export async function verifyTwoFactorCode(
  email: string,
  code: string
): Promise<VerifyTwoFactorCodeResponse> {
  try {
    const response = await fetch(`${API_URL}/auth/twofactor/verify`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, code }),
    });

    if (!response.ok) {
      if (response.status === 400) {
        const error = await response.json().catch(() => ({ message: 'Invalid input' }));
        throw new Error(error.message || 'Invalid input');
      }
      if (response.status === 401) {
        const error = await response.json().catch(() => ({ message: 'Invalid verification code' }));
        throw new Error(error.message || 'Invalid verification code. Please try again.');
      }
      if (response.status === 429) {
        const error = await response.json().catch(() => ({ message: 'Too many attempts' }));
        throw new Error(error.message || 'Too many attempts. Please try again in an hour.');
      }
      throw new Error('Failed to verify code');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Network error: Failed to verify code');
  }
}

